<template>
  <div class="control-panel">
    <font-awesome-icon
      icon="star"
      size="lg"
      class="float-left"
      v-bind:class="['control-btn', { favorite: scene.favorite }]"
      @click="favoriteSceneClicked" />
    <font-awesome-icon
      icon="times"
      size="2x"
      class="float-right control-btn"
      @click="closePanel"
    />
    <font-awesome-icon :icon="scene.icon" size="3x" />
    <h2>{{ scene.name }} </h2>
    <strong>{{ $t('scene-settings') }}</strong>
    <hr />
    <div class="mt-3">
      <div v-for="service in getServicesByIds(scene.services)" :key="service.id">
        <font-awesome-icon
          icon="trash-alt"
          class="float-right control-btn"
          :title="$t('remove-service')"
          @click="removeServiceClicked(service.id)"
        />
        <h4>{{ service.name }}</h4>
        <p>
          {{ service.fullName }}
        </p>
        <scene-actors :service="service" :scene="scene" showLabels />
      </div>
    </div>
    <b-button variant="primary" @click="addServiceClicked">
      <font-awesome-icon icon="plus" />
      {{ $t('add-service') | capitalize }}
    </b-button>
    <hr />
    <b-button variant="primary" @click="updateSceneClicked" class="mr-2">
      <font-awesome-icon icon="pen" />
      {{ $t('edit-scene') | capitalize }}
    </b-button>
    <b-button variant="primary" @click="shareSceneClicked" class="mr-2">
      <font-awesome-icon icon="share-alt" />
      {{ $t('share') | capitalize }}
    </b-button>
    <b-button variant="danger" @click="removeSceneClicked">
      <font-awesome-icon icon="trash-alt" />
      {{ $t('remove-scene') | capitalize }}
    </b-button>
    <add-service-modal ref="addServiceModal" :scene="scene" @service-picked="servicePicked" />
    <scene-modal-form ref="updateSceneModal" :title="$t('edit-scene')" @ok="updateSceneOk" />
    <user-share-modal
      ref="userShareModal"
      :title="$t('of-scene')"
      :assignmentsGetFunc="getSceneAssignments"
      :assignmentsDeleteFunc="deleteSceneAssignment"
      :assignments-create-func="createSceneAssignment"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import ControlPanelMixin from '@/mixins/ControlPanel';
import SceneActors from '@/components/scenes/scene/SceneActors.vue';
import SceneModalForm from '@/components/scenes/scene/SceneModalForm.vue';
import AddServiceModal from '@/components/scenes/scene/AddServiceModal.vue';
import config from '@/config';
import axios from 'axios';
import UserShareModal from '@/components/sharing/userShareModal.vue';

export default {
  name: 'SceneControlPanel',
  mixins: [ControlPanelMixin],
  props: {
    scene: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState('snapshot', [
      'currentSnapshot',
    ]),
    ...mapGetters([
      'getServices',
      'getServiceById',
      'getServicesByIds',
    ]),
  },
  methods: {
    ...mapActions([
      'addServiceIntoScene',
      'removeServiceFromScene',
      'removeScene',
      'updateScene',
      'setFavoriteScene',
    ]),
    addServiceClicked() {
      this.$refs.addServiceModal.show();
    },
    removeServiceClicked(serviceId) {
      this.removeServiceFromScene({ serviceId, sceneId: this.scene.id });
    },
    removeSceneClicked() {
      this.removeScene(this.scene);
      this.closePanel();
    },
    updateSceneClicked() {
      this.$refs.updateSceneModal.initData({
        name: this.scene.name,
        icon: this.scene.icon,
      });
      this.$refs.updateSceneModal.show();
    },
    updateSceneOk({ name, icon }) {
      this.updateScene({ name, icon, sceneId: this.scene.id });
    },
    servicePicked(servicesIds) {
      this.addServiceIntoScene({ serviceId: servicesIds[0], sceneId: this.scene.id });
      this.$refs.addServiceModal.hide();
    },
    favoriteSceneClicked() {
      if (!this.scene.favorite) {
        this.setSceneAsFavorite();
      } else {
        this.unsetSceneAsFavorite();
      }
    },
    setSceneAsFavorite() {
      const url = new URL(
        config.ICOOL.API.FAVORITE_OBJECTS_PATH(this.currentSnapshot.id),
        config.ICOOL.BACKEND_HOST,
      ).href;
      const data = {
        content_type: 'scene',
        object_id: this.scene.id,
      };
      axios.post(url, data).then((response) => {
        this.setFavoriteScene({ scene: this.scene, favorite: true });
      }).catch((error) => {
      }).finally(() => {
      });
    },
    unsetSceneAsFavorite() {
      const url = new URL(
        config.ICOOL.API.FAVORITE_OBJECT_PATH(this.currentSnapshot.id, 'scene', this.scene.id),
        config.ICOOL.BACKEND_HOST,
      ).href;
      axios.delete(url).then((response) => {
        this.setFavoriteScene({ scene: this.scene, favorite: false });
      }).catch((error) => {
      }).finally(() => {
      });
    },
    shareSceneClicked() {
      this.$refs.userShareModal.show();
    },
    getSceneAssignments() {
      const url = new URL(
        config.ICOOL.API.SCENE_ASSIGNMENTS_PATH(this.scene.id),
        config.ICOOL.BACKEND_HOST,
      ).href;
      return new Promise((resolve, reject) => {
        axios
          .get(url)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            console.log(error);
            reject();
          });
      });
    },
    deleteSceneAssignment(assignmentId) {
      const url = new URL(
        config.ICOOL.API.SCENE_ASSIGNMENT_PATH(this.scene.id, assignmentId),
        config.ICOOL.BACKEND_HOST,
      ).href;
      return new Promise((resolve, reject) => {
        axios
          .delete(url)
          .then((response) => {
            resolve();
          })
          .catch((error) => {
            console.log(error);
            reject();
          });
      });
    },
    createSceneAssignment(userId, permissions) {
      const url = new URL(
        config.ICOOL.API.SCENE_ASSIGNMENTS_PATH(this.scene.id),
        config.ICOOL.BACKEND_HOST,
      ).href;
      return new Promise((resolve, reject) => {
        axios
          .post(url, { user: userId, permissions, scene: this.scene.id })
          .then((response) => {
            resolve();
          })
          .catch((error) => {
            console.log(error);
            reject();
          });
      });
    },
  },
  components: {
    AddServiceModal,
    SceneActors,
    SceneModalForm,
    UserShareModal,
  },
};
</script>

<style scoped>

</style>
